// Quality inspections — data access (docs/design/quality.md, PR 2).
//
//   list     rpc inspection_order_page(company, status, term, after_at, after_id, limit 51):
//            keyset (created_at desc, id desc) on inspection_orders_list_idx / _open_idx.
//   detail   rpc inspection_order_document(id): header, results, lot, locations, source.
//   writes   save_inspection_results / decide_inspection / cancel_inspection with the
//            order's revision; create_inspection_order for on-demand inspections.
// Numbers travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { qualityKeys, type CharacteristicKind } from "./api";
import type { inspectionOrderTriggerValues } from "@patronix/domain/list";

/** Inspection orders: the plan triggers plus spot_check (block_for_inspection). */
export type InspectionOrderTrigger = (typeof inspectionOrderTriggerValues)[number];
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const inspectionStatuses = ["open", "in_progress", "decided", "cancelled"] as const;
export type InspectionStatus = (typeof inspectionStatuses)[number];
export const inspectionFilters = ["open", "decided", "cancelled", "all"] as const;
export type InspectionFilter = (typeof inspectionFilters)[number];
export const isInspectionFilter = (v: unknown): v is InspectionFilter => (inspectionFilters as readonly unknown[]).includes(v);
export type InspectionDecision = "accept" | "reject" | "partial";

export const statusTone = { open: "info", in_progress: "warning", decided: "success", cancelled: "neutral" } as const;
export const decisionTone = { accept: "success", reject: "danger", partial: "warning" } as const;

export const INSPECTION_PAGE = 50;
const STALE = 30_000;

export interface InspectionRow {
  id: string;
  number: string;
  status: InspectionStatus;
  trigger: InspectionOrderTrigger;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_code: string | null;
  qty: string | number;
  base_uom_code: string;
  warehouse_code: string;
  decision: InspectionDecision | null;
  source_type: "purchase_receipt" | "production_posting" | "manual" | "sales_return_receipt";
  source_number: string | null;
  created_at: string;
  result_count: number;
  entered_count: number;
  nok_count: number;
}

export interface InspectionResult {
  seq: number;
  characteristic_code: string;
  characteristic_name: string;
  kind: CharacteristicKind;
  uom_code: string | null;
  decimals: number;
  target: string | number | null;
  lower_limit: string | number | null;
  upper_limit: string | number | null;
  required: boolean;
  value_numeric: string | number | null;
  value_pass: boolean | null;
  value_text: string | null;
  conformity: "ok" | "nok" | null;
  entered_at: string | null;
}

export interface InspectionDocument {
  id: string;
  number: string;
  status: InspectionStatus;
  trigger: InspectionOrderTrigger;
  company_id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  /** Spot check: why the stock was blocked. */
  reason?: string | null;
  lot_id: string | null;
  lot_code: string | null;
  /** Serial-controlled item: the units under inspection. */
  serial_numbers?: string[] | null;
  lot_quality_status: "released" | "in_inspection" | "rejected" | null;
  qty: string | number;
  sample_size: string | number | null;
  warehouse_id: string;
  warehouse_code: string;
  quarantine_location_id: string;
  quarantine_location_code: string;
  putaway_location_id: string;
  putaway_location_code: string;
  source_type: InspectionRow["source_type"];
  source_id: string | null;
  source_number: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  decision: InspectionDecision | null;
  accepted_qty: string | number;
  rejected_qty: string | number;
  decided_at: string | null;
  decided_by_name: string | null;
  note: string | null;
  revision: number;
  created_at: string;
  closed_at: string | null;
  ncrs: { id: string; number: string; status: "open" | "dispositioned" | "closed" | "cancelled"; qty: string | number; disposition: string | null }[];
  results: InspectionResult[];
}

export interface InspectionListParams {
  companyId: string;
  status: InspectionFilter;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface ResultInput {
  seq: number;
  value_numeric?: string | null;
  value_pass?: boolean | null;
  value_text?: string | null;
}

export const inspectionKeys = {
  all: [...qualityKeys.all, "inspections"] as const,
  list: (p: InspectionListParams) => [...qualityKeys.all, "inspections", "list", p] as const,
  detail: (id: string) => [...qualityKeys.all, "inspections", "detail", id] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}
const demoOff = () => new Error("Quality is not available in demo mode");

/** Cursor "created_at|id". */
export async function listInspections(
  p: InspectionListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = INSPECTION_PAGE,
): Promise<Page<InspectionRow>> {
  if (isDemoMode || !p.companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("inspection_order_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (InspectionRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getInspection(id: string, signal: AbortSignal): Promise<InspectionDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("inspection_order_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as InspectionDocument | null) ?? null;
}

export function inspectionListQuery(p: InspectionListParams) {
  return infiniteQueryOptions({
    queryKey: inspectionKeys.list(p),
    queryFn: ({ pageParam, signal }) => listInspections(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<InspectionRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!p.companyId,
  });
}

export function inspectionDetailQuery(id: string) {
  return queryOptions({ queryKey: inspectionKeys.detail(id), queryFn: ({ signal }) => getInspection(id, signal), staleTime: STALE });
}

export async function saveResults(id: string, revision: number, results: ResultInput[]): Promise<{ revision: number }> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("save_inspection_results", { p_id: id, p_revision: revision, p_results: results });
  if (error) throw error;
  return data as { revision: number };
}

export async function decideInspection(
  id: string,
  revision: number,
  decision: InspectionDecision,
  acceptedQty: string | null,
  putawayLocationId: string | null,
  note: string | null,
  rejectedSerials: string[] | null = null,
) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("decide_inspection", {
    p_id: id,
    p_revision: revision,
    p_decision: decision,
    p_accepted_qty: acceptedQty,
    p_putaway_location_id: putawayLocationId,
    p_note: note,
    p_rejected_serials: rejectedSerials,
  });
  if (error) throw error;
}

export async function cancelInspection(id: string, revision: number, note: string | null) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("cancel_inspection", { p_id: id, p_revision: revision, p_note: note });
  if (error) throw error;
}

export async function createInspection(
  companyId: string,
  header: {
    item_id: string;
    warehouse_id: string;
    location_id: string;
    lot_id: string | null;
    project_id?: string | null;
    qty: string;
    note: string | null;
    serials?: string[];
  },
): Promise<{ id: string; number: string }> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("create_inspection_order", { p_company_id: companyId, p_header: header });
  if (error) throw error;
  return data as { id: string; number: string };
}

export interface BlockResult {
  id: string;
  number: string;
  moved_to_quarantine: boolean;
  transaction_ids: number[];
  /** Reservations no longer covered by reservable stock, per warehouse. */
  reservation_shortfall: { warehouse_code: string; reserved: number | string; reservable: number | string }[];
}

/** Spot check: block stock where it lies (block_for_inspection) — moved to the
 *  warehouse's quarantine location, or the lot blocked in place; opens an
 *  inspection order (trigger spot_check, plan optional). */
export async function blockForInspection(
  companyId: string,
  header: {
    item_id: string;
    warehouse_id: string;
    location_id: string;
    lot_id: string | null;
    project_id?: string | null;
    qty: string;
    reason: string;
    serials?: string[];
  },
): Promise<BlockResult> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("block_for_inspection", { p_company_id: companyId, p_header: header });
  if (error) throw error;
  return data as BlockResult;
}

/** Client-side conformity preview while typing (the database has the last word). */
export function previewConformity(r: InspectionResult, value: { num?: string; pass?: boolean | null; text?: string }): "ok" | "nok" | null {
  if (r.kind === "measure") {
    const v = value.num?.trim().replace(",", ".");
    if (!v || !/^-?\d+(\.\d+)?$/.test(v)) return null;
    const n = Number(v);
    if (r.lower_limit !== null && n < Number(r.lower_limit)) return "nok";
    if (r.upper_limit !== null && n > Number(r.upper_limit)) return "nok";
    return "ok";
  }
  if (r.kind === "attribute") return value.pass === undefined || value.pass === null ? null : value.pass ? "ok" : "nok";
  return value.text?.trim() ? "ok" : null;
}

const rules: [RegExp, string][] = [
  [/Give the reason for blocking/, "quality.spot.err.reason"],
  [/has no quarantine location/, "quality.spot.err.noQuarantine"],
  [/already in quarantine/, "quality.spot.err.inQuarantine"],
  [/Serial .+ appears twice/, "quality.err.serialTwice"],
  [/already on an open inspection or nonconformance/, "quality.err.serialBusy"],
  [/Give the \d+ serial numbers/, "quality.err.serialCount"],
  [/Serial .+ is not in stock there/, "quality.err.serialStock"],
  [/is not serial-controlled/, "quality.err.notSerial"],
  [/is not part of this inspection|units not rejected|has no serial numbers/, "quality.ins.err.partialSerials"],
  [/Enter the required results first/, "quality.ins.err.required"],
  [/changed by someone else/, "quality.err.stale"],
  [/partial decision/, "quality.ins.err.partial"],
  [/Results were entered/, "quality.ins.err.cancelResults"],
  [/cannot be changed/, "quality.ins.err.final"],
  [/no active on-demand inspection plan/, "quality.ins.err.noPlan"],
  [/on hand there/, "quality.ins.err.onHand"],
  [/Choose the lot/, "quality.ins.err.lot"],
  [/Putaway location not found/, "quality.ins.err.putaway"],
  [/location is being counted|is being counted/, "quality.ins.err.counted"],
  [/permission|row-level security|42501/i, "quality.ins.err.permission"],
];

export function inspectionErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const required = /Enter the required results first: (.+)$/.exec(message);
  if (required) return tr("quality.ins.err.required", { codes: required[1] ?? "" });
  const onHand = /Only ([\d.]+) on hand there/.exec(message);
  if (onHand) return tr("quality.ins.err.onHand", { qty: onHand[1] ?? "" });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
